
import React from 'react'
import { Grid, Typography, Button, PaletteMode } from '@mui/material'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { withComponentContext } from 'widgets-shared/core'
import { compose } from 'widgets-shared/core'
import Disclaimer from 'smartstream-widgets/controls/Disclaimer/Disclaimer'
import GSCTA from 'smartstream-widgets/controls/GSCTA/GSCTA'
import { getBookmakerLogoUrlByTheme } from 'smartstream-widgets/utils/logos'
import { Bookmakers } from 'smartstream-widgets/types/sharedModule/core'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme';

type Props = {
  sourceId: string,
  classes: {
    [K in string]: string
  },
  bookmakers: Bookmakers
  config: {
    BASE_ASSET_PATH: string,
    defaultLink: string,
    target: string
  },
  disclaimer: string,
  footerLink?: string,
  footerText: {
    betCTA?: string,
    disclaimer: string,
    optOutText?: string,
    helperInfo?: {
      [K in string]: string
    },
    oddsByLabel: string,
    poweredBy?: string
  },
  showCTA?: boolean,
  theme: Theme,
  themeMode: PaletteMode,
  isSingleFixture: boolean
}


const styles = (theme: Theme) => createStyles({
  betBookmaker: {},
  betCTA: {
    padding: 0,
    textDecoration: 'underline'
  },
  betLabel: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 12
  },
  bookmakerContainer: {
    padding: `0px 9px`,
    '& $betBookmaker, & $poweredBy': {
      '&:only-child': {
        margin: 'auto',
        width: 'auto',
      }
    }
  },
  bookmakerLogo: {
    height: 12,
    margin: 5
  },
  ctaLabel: {},
  ctaRoot: {
    lineHeight: 1,
    minHeight: 'auto',
    minWidth: 'auto',
    padding: `0px 4px 2px`
  },
  disclaimerRoot: {},
  disclaimerText: {},
  oddsByBookmaker: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    fontSize: 9,
    justifyContent: 'center',
    whiteSpace: 'nowrap'
  },
  poweredBy: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    fontSize: 9,
    justifyContent: 'center',
    padding: `4px 0px`,
    fontFamily: 'Klarheit Kurrent'
  },
  poweredByLogo: {
    height: 15
  },
  root: {
    bottom: 0,
    boxSizing: 'border-box',
    padding: `5px 0px`,
    position: 'absolute',
    textAlign: 'center',
    width: '100%'
  },
  logoButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  muiButtonLabel: {
    display: 'inherit',
    alignItems: 'inherit',
    justifyContent: 'inherit',
    width: '100%'
  },
  oddsByContainer: {}
})

export function BookmakerFooter(props: Props) {
  const {
    sourceId,
    classes,
    disclaimer,
    footerLink,
    bookmakers = {},
    config: {
      BASE_ASSET_PATH,
      defaultLink,
      target = '_blank'
    },
    footerText,
    showCTA = false,
    theme,  
    themeMode,
    isSingleFixture
  } = props


  theme.palette.mode = themeMode || theme.palette.mode || 'light'
  const bookmakerLogoUrl = bookmakers[sourceId] && bookmakers[sourceId].logo && getBookmakerLogoUrlByTheme(bookmakers[sourceId], theme)
  const bookmakerLink = bookmakers[sourceId] && bookmakers[sourceId].link
  const CTALink = bookmakers[sourceId] && bookmakers[sourceId].link || defaultLink
  const name = bookmakers[sourceId] && bookmakers[sourceId].name
  const bookmakerLogoNode = (
    <img
      src={bookmakerLogoUrl}
      alt={name}
      className={classes.bookmakerLogo}
    />
  )

  return (
    <footer className={classes.root} data-testid="bookmaker-footer">
      <Grid container alignItems='center' justifyContent='space-between' className={classes.bookmakerContainer}>
        {
          bookmakerLogoUrl ?
            <Grid className={classes.betBookmaker}>
              {
                (!showCTA && footerText.betCTA && CTALink) ?
                  <Grid container className={classes.oddsByContainer}>
                    <Typography className={classes.oddsByBookmaker}>
                      {footerText.betCTA}
                    </Typography>
                    <Button
                      className={classes.betCTA}
                      variant='text'
                      color='primary'
                      href={CTALink}
                      target={target}
                    >
                      <span className={`${classes.betLabel}`}>
                        {bookmakerLogoNode}
                        </span>
                    </Button>
                  </Grid> :
                  bookmakerLink ?
                    <Grid container className={classes.oddsByContainer}>
                      {footerText.oddsByLabel &&
                        <Typography className={classes.oddsByBookmaker}>
                          {footerText.oddsByLabel}
                        </Typography>
                      }
                      <Button
                        className={classes.logoButton}
                        variant='text'
                        color='primary'
                        href={CTALink}
                        target={target}
                      >
                       <span className={`${classes.betLabel}`}>{bookmakerLogoNode}</span> 
                      </Button>
                    </Grid>
                    :
                    <Typography className={classes.oddsByBookmaker}>
                      {footerText.oddsByLabel}&nbsp;{bookmakerLogoNode}
                    </Typography>
              }
            </Grid> :
            <Typography align='center' className={classes.poweredBy}>
              <img
                className={classes.poweredByLogo}
                src={`${BASE_ASSET_PATH}/img/genius-sports-logos/genius-sports-logo-marque-${theme.palette.mode}.svg`}
                alt='Genius Sports'
              />
              {footerText.poweredBy}
            </Typography>
        }
        {
          CTALink && showCTA &&
          <GSCTA classes={{ root: classes.ctaRoot, label: classes.ctaLabel }} />
        }
      </Grid>
      <Disclaimer
        classes={{ root: classes.disclaimerRoot, disclaimerText: classes.disclaimerText }}
        disclaimer={disclaimer}
        footerLink={footerLink}
        isSingleFixture={isSingleFixture}
      />
    </footer >
  )
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'BookmakerFooter', withTheme: true, defaultTheme: getDefaultTheme })
)(BookmakerFooter)
